// Client-side row validations + duplicate detection.
// Required fields: Product, Length, Location. Length must resolve to one of the matched
// product's DB lengths. Sets row.__issues (field -> level) and returns per-row reports.
// Messages are emitted as translation keys (+ params) so the UI can localise them; the
// keys live under the `validation.*` prefix in the `inventory-import` namespace.

import { productById } from './catalog';
import { isBareNumberLength } from '~/utils/lengthParser';
import { FIELD_BY_KEY, UNIT_CODES } from './targetFields';
import { parseImportNumber } from './numberParse';
import { isUnitAllowedForCategory } from '~/utils/productUnitPolicy';
import type { CellIssue } from './types';
import type { GridRow } from './rows';

const NUMERIC_FIELDS = [
  'stock',
  'leadTimeDays',
  'replenishmentRate',
  'moq',
  'criticalStock',
  'lowStock',
];

// O(1) unit lookup for the per-row validation loop (UNIT_CODES is already upper-case).
const UNIT_CODE_SET = new Set(UNIT_CODES);

export interface RowReport {
  rowId: number;
  issues: CellIssue[];
}

export interface ValidationResult {
  reports: RowReport[];
  errorCount: number;
  warningCount: number;
}

function isBlank(value: unknown): boolean {
  return value === null || value === undefined || String(value).trim() === '';
}

function asNumber(value: unknown): number | null {
  return parseImportNumber(value);
}

/** A short hint of a product's first few available lengths, for the "not valid" message. */
function availableLengthsHint(productId: string): string {
  const product = productById(productId);
  if (!product || product.lengths.length === 0) return '';
  const shown = product.lengths
    .slice(0, 6)
    .map((l) => l.displayValue)
    .join(', ');
  return product.lengths.length > 6 ? `${shown}, …` : shown;
}

/** Validate all rows, writing __issues onto each row, and return a summary + reports. */
export function validateRows(rows: GridRow[]): ValidationResult {
  const reports: RowReport[] = [];
  const issuesByRow = new Map<number, CellIssue[]>();

  for (const row of rows) {
    const issues: CellIssue[] = [];
    row.__issues = {};

    const add = (issue: CellIssue) => {
      issues.push(issue);
      const existing = row.__issues[issue.field];
      if (
        !existing ||
        (existing.level !== 'error' && issue.level === 'error')
      ) {
        row.__issues[issue.field] = {
          level: issue.level,
          messageKey: issue.messageKey,
          params: issue.params,
        };
      }
    };

    // Product (required): must resolve to a catalog product.
    const productResolved = Boolean(row.product);
    // Gate on the RESOLVED product (auto-matched or user-picked), not the raw descriptor — a manual
    // pick sets row.product without touching descriptor.
    if (!productResolved) {
      if (isBlank(row.descriptor)) {
        add({
          field: 'product',
          level: 'error',
          messageKey: 'validation.product_required',
        });
      } else {
        add(
          row.__status === 'ambiguous'
            ? {
                field: 'product',
                level: 'warning',
                messageKey: 'validation.product_ambiguous',
              }
            : {
                field: 'product',
                level: 'error',
                messageKey: 'validation.product_no_match',
              }
        );
      }
    }

    if (productResolved && row.__ignoredWords) {
      add({
        field: 'product',
        level: 'warning',
        messageKey: 'validation.product_words_ignored',
        params: { words: row.__ignoredWords },
      });
    }
    if (productResolved && row.__assumedSpecies) {
      add({
        field: 'product',
        level: 'warning',
        messageKey: 'validation.product_species_assumed',
        params: { species: row.__assumedSpecies },
      });
    }

    // Length (required): must resolve to one of the product's DB lengths.
    if (isBlank(row.length)) {
      add({
        field: 'lengthInches',
        level: 'error',
        messageKey: 'validation.length_required',
      });
    } else if (productResolved && !row.lengthInches) {
      const hint = availableLengthsHint(String(row.product));
      add(
        hint
          ? {
              field: 'lengthInches',
              level: 'error',
              messageKey: 'validation.length_not_valid',
              params: { length: String(row.length), available: hint },
            }
          : {
              field: 'lengthInches',
              level: 'error',
              messageKey: 'validation.length_unreadable',
              params: { length: String(row.length) },
            }
      );
    } else if (row.lengthInches && isBareNumberLength(String(row.length))) {
      // Valid, but the value had no unit, so the parser assumed feet (mirrors the backend). Warn so
      // the user can confirm or add an explicit unit; the row is still importable.
      add({
        field: 'lengthInches',
        level: 'warning',
        messageKey: 'validation.length_unit_assumed',
        params: {
          length: String(row.length),
          inches: String(row.lengthInches),
        },
      });
    }

    // Gate on the RESOLVED location (auto-matched or user-picked), not the raw locationCode — a
    // manual pick sets row.location without touching locationCode.
    if (!row.location) {
      if (isBlank(row.locationCode)) {
        add({
          field: 'location',
          level: 'error',
          messageKey: 'validation.location_required',
        });
      } else {
        add({
          field: 'location',
          level: 'error',
          messageKey: 'validation.location_not_found',
        });
      }
    }

    if (row.__stockNote) {
      add({
        field: 'stock',
        level: row.__stockNote.level,
        messageKey: row.__stockNote.messageKey,
        params: row.__stockNote.params,
      });
    }

    // Optional numeric fields must parse and have a sane sign (quantities >= 0).
    for (const key of NUMERIC_FIELDS) {
      if (isBlank(row[key])) continue;
      const value = asNumber(row[key]);
      if (value === null) {
        add({
          field: key,
          level: 'error',
          messageKey: 'validation.must_be_number',
          params: { field: `fields.${FIELD_BY_KEY[key].key}` },
        });
      } else if (value < 0) {
        add({
          field: key,
          level: 'error',
          messageKey: 'validation.must_be_non_negative',
          params: { field: `fields.${FIELD_BY_KEY[key].key}` },
        });
      } else if (key === 'leadTimeDays' && !Number.isInteger(value)) {
        // Lead time is a count of days — the backend stores it as an int, so a fractional value
        // would be silently truncated. Block it so the user enters a whole number.
        add({
          field: key,
          level: 'error',
          messageKey: 'validation.must_be_whole_number',
          params: { field: `fields.${FIELD_BY_KEY[key].key}` },
        });
      }
    }

    // Unit (optional) must be a known code when present. The mutation's unit is a UnitCode enum and
    // the payload builder drops an unrecognised unit, falling back to the product's standard unit —
    // so flag it here to surface the problem instead of silently importing under the wrong unit.
    const enteredUnit = String(row.unit ?? '')
      .trim()
      .toUpperCase();
    if (!isBlank(row.unit) && !UNIT_CODE_SET.has(enteredUnit)) {
      add({
        field: 'unit',
        level: 'error',
        messageKey: 'validation.unit_not_valid',
        params: { unit: String(row.unit) },
      });
    } else if (
      !isBlank(row.unit) &&
      !isUnitAllowedForCategory(
        productById(String(row.product ?? ''))?.category,
        enteredUnit
      )
    ) {
      add({
        field: 'unit',
        level: 'error',
        messageKey: 'validation.unit_not_allowed_for_product',
        params: { unit: enteredUnit },
      });
    }

    // Critical must be below Low (when both provided). A Low of 0 means "no low threshold set"
    // (matches the backend guard), so don't flag critical >= low in that case.
    const critical = asNumber(row.criticalStock);
    const low = asNumber(row.lowStock);
    if (critical !== null && low !== null && low > 0 && critical >= low) {
      add({
        field: 'criticalStock',
        level: 'error',
        messageKey: 'validation.critical_below_low',
      });
    }

    issuesByRow.set(row.__id, issues);
  }

  // Duplicate product + location + length: the backend rejects these (like the CSV path), so block
  // them here too. But the backend dedups ONLY over rows that are otherwise importable (sign-valid +
  // resolved), so an independently-invalid row must NOT turn a clean sibling into a duplicate. Run
  // dedup after per-row validation and skip rows that already carry a blocking error.
  const hasBlockingError = (row: GridRow) =>
    Object.values(row.__issues).some((issue) => issue.level === 'error');
  const seen = new Map<string, number>();
  const duplicateIds = new Set<number>();
  for (const row of rows) {
    if (!row.product || hasBlockingError(row)) continue;
    const loc = String(row.location || row.locationCode || '').toLowerCase();
    const key = `${row.product}|${loc}|${row.lengthInches || String(row.length ?? '').toLowerCase()}`;
    if (seen.has(key)) {
      duplicateIds.add(row.__id);
      duplicateIds.add(seen.get(key)!);
    } else {
      seen.set(key, row.__id);
    }
  }

  let errorCount = 0;
  let warningCount = 0;
  for (const row of rows) {
    if (duplicateIds.has(row.__id)) {
      const dup: CellIssue = {
        field: 'product',
        level: 'error',
        messageKey: 'validation.duplicate',
      };
      row.__issues.product = { level: dup.level, messageKey: dup.messageKey };
      issuesByRow.get(row.__id)?.push(dup);
    }
    // Count the kept issues (one per field), so the summary matches the highlighted cells rather
    // than the raw push count (a field can appear twice but only its highest level is shown).
    for (const issue of Object.values(row.__issues)) {
      if (issue.level === 'error') errorCount++;
      else warningCount++;
    }
    const issues = issuesByRow.get(row.__id) ?? [];
    if (issues.length > 0) reports.push({ rowId: row.__id, issues });
  }

  return { reports, errorCount, warningCount };
}
